/**
 * 应用入口文件 —— 整个 Todo 应用的起点
 *
 * 【这个文件做什么？】
 * 1. 导入 React 和渲染 API
 * 2. 获取 HTML 中的根 DOM 节点（#root）
 * 3. 将 React 组件树渲染到这个根节点中
 *
 * 【什么是"入口文件"？】
 * 就像一本书的目录页，入口文件是应用启动时第一个执行的文件。
 * Vite（构建工具）会从这里开始，打包所有依赖的代码。
 *
 * 【渲染流程】
 * index.html 中的 <div id="root"></div>
 *        ↓
 * main.tsx 找到这个 div
 *        ↓
 * createRoot() 创建 React 根节点
 *        ↓
 * root.render(<App />) 将 App 组件渲染到根节点中
 *        ↓
 * React 接管这个 div，管理其中的所有 DOM 操作
 *
 * 【StrictMode 是什么？】
 * StrictMode 是 React 的开发辅助工具，在开发环境下会：
 * - 检测不安全的生命周期方法
 * - 检测过时的 API 用法
 * - 检测意外的副作用（每个 effect 执行两次）
 * 注意：StrictMode 只在开发环境生效，生产构建中不起作用。
 */

// StrictMode: React 的严格模式，帮助发现潜在问题
import { StrictMode } from "react";
// createRoot: React 18 的新渲染 API（替代旧的 ReactDOM.render）
import { createRoot } from "react-dom/client";
// App: 应用的根组件，包含路由、布局等
import App from "./App";
// 全局样式（Tailwind CSS 的指令和自定义样式）
import "./styles/global.css";

// 获取 HTML 中的根元素（public/index.html 中的 <div id="root">）
const rootElement = document.getElementById("root");

// 安全检查：确保根元素存在后再渲染
// 如果 HTML 中没有 id="root" 的元素，不会报错
if (rootElement) {
  // createRoot 创建一个 React 根节点
  // 这是 React 18 引入的并发渲染（Concurrent Rendering）的入口
  const root = createRoot(rootElement);

  // root.render 将 React 组件树渲染到 DOM 中
  // StrictMode 包裹 App，启用严格模式检查
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
